import { Button, Typography, VisibilityIcon } from "@povio/ui";
import { useEffect, useMemo } from "react";

import { CancelIcon } from "@/assets/icons/general/CancelIcon";
import { RefreshIcon } from "@/assets/icons/general/Refresh";
import type { NotificationsModels } from "@/data/notifications/notifications.models";
import { DateUtils } from "@/util/date.utils";

interface IProps {
  notification: NotificationsModels.NotificationDto;
  markNotificationAsRead?: () => void;
  onNotificationClicked?: (notification: NotificationsModels.NotificationDto) => void;
  onDelete?: () => void;
  clickable?: boolean;
}

const getNotificationIcon = (type: string) => {
  const iconMap: Record<string, typeof VisibilityIcon> = {
    ResumeExtractionFailedNotification: RefreshIcon,
  };
  return iconMap[type] ?? VisibilityIcon;
};

const NotificationItem = ({
  notification,
  onNotificationClicked,
  markNotificationAsRead,
  onDelete,
  clickable,
}: IProps) => {
  const notificationIcon = useMemo(() => getNotificationIcon(notification.type), [notification.type]);

  useEffect(() => {
    if (clickable || notification.readAt) {
      return;
    }
    markNotificationAsRead?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification, clickable, markNotificationAsRead]);

  return (
    <div className="relative flex flex-col gap-3-5 rounded-3xl bg-elevation-fill-default-1 p-4 transition-colors">
      <div className="flex flex-col gap-1">
        <Typography
          size="label-4"
          variant="prominent-1"
          className="text-text-default-1"
        >
          {notification.data.title}
        </Typography>
        <Typography
          size="label-3"
          className="text-text-default-3"
        >
          {DateUtils.formatRelativeDate(notification.createdAt)}
        </Typography>
      </div>
      <Typography
        size="label-3"
        className="text-text-default-2"
      >
        {notification.data.message}
      </Typography>
      <div className="flex flex-row items-start justify-between gap-3-5">
        {clickable && (
          <Button
            icon={notificationIcon}
            variant="outlined"
            color="secondary"
            size="s"
            width={onDelete ? "fill" : "hug"}
            onPress={() => {
              markNotificationAsRead?.();
              onNotificationClicked?.(notification);
            }}
          >
            {notification.data.action?.text || "View"}
          </Button>
        )}
        {onDelete && (
          <Button
            variant="outlined"
            color="secondary"
            size="s"
            width={clickable ? "fill" : "hug"}
            icon={CancelIcon}
            onPress={onDelete}
          >
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
